/**
 * Utility functions for user sorting and display
 */

interface UserWithBusinessRoles {
    firstName: string;
    lastName: string;
    businessRoles?: {
        role?: string;
        isBusinessAdmin?: boolean;
    }[];
}

/**
 * Checks if a user has an admin business role
 */
export const isUserAdmin = (user: UserWithBusinessRoles): boolean => {
    return user.businessRoles?.some(role => role.isBusinessAdmin) ?? false;
};

/**
 * Sorts users by:
 * 1. BusinessRole (admin users first)
 * 2. First name (alphabetically)
 * 3. Last name (alphabetically)
 *
 * @param users Array of users to sort
 * @returns Sorted array of users (creates a new array, does not mutate original)
 */
export const sortUsers = <T extends UserWithBusinessRoles>(users: T[]): T[] => {
    return [...users].sort((a, b) => {
        // First, sort by admin status (admins first)
        const aIsAdmin = isUserAdmin(a);
        const bIsAdmin = isUserAdmin(b);

        if (aIsAdmin && !bIsAdmin) return -1;
        if (!aIsAdmin && bIsAdmin) return 1;

        // Then sort by first name
        const firstNameCompare = a.firstName.localeCompare(b.firstName);
        if (firstNameCompare !== 0) return firstNameCompare;

        // Finally sort by last name
        return a.lastName.localeCompare(b.lastName);
    });
};

/**
 * Sorts user share objects that have fullName and isAdmin properties
 */
interface UserShareWithAdmin {
    fullName: string;
    isAdmin?: boolean;
}

export const sortUserShares = <T extends UserShareWithAdmin>(shares: T[]): T[] => {
    return [...shares].sort((a, b) => {
        // First, sort by admin status (admins first)
        const aIsAdmin = a.isAdmin ?? false;
        const bIsAdmin = b.isAdmin ?? false;

        if (aIsAdmin && !bIsAdmin) return -1;
        if (!aIsAdmin && bIsAdmin) return 1;

        // Then sort by full name
        return a.fullName.localeCompare(b.fullName);
    });
};
